import { Navigate, useNavigate } from 'react-router-dom';

import { useIdentifyUser } from '@/lib/posthog/hooks/useIdentifyUser';
import { useLogoutMutation } from '@/modules/auth/mutations/useLogoutMutation';
import { useCurrentTenantQuery } from '@/modules/companies/queries/useCurrentTenantQuery';
import { SignupFlowShell } from '@/modules/signup-flow/components/layout/SignupFlowShell';
import { StepTransition } from '@/modules/signup-flow/components/layout/StepTransition';
import { SignupFlowRouter } from '@/modules/signup-flow/components/signupFlowRouter/SignupFlowRouter';
import {
  getSignupFlowStep,
  signupFlowStep,
} from '@/modules/signup-flow/signupFlowSteps';
import { useIsSlackConnected } from '@/modules/user-external-accounts/hooks/useIsSlackConnected';

/**
 * Signup frame around the persisted step. The Slack step has two phases
 * (connect, then channel once OAuth returns) that share one persisted step,
 * so the transition key and the wide layout follow the connection state too.
 */
export const SignupFlowLayout = () => {
  // New users land here straight after sign-up, before the portal's layouts;
  // identify them now so onboarding drop-offs keep their email domain and
  // company on the PostHog person.
  useIdentifyUser();
  const navigate = useNavigate();
  const currentTenantQuery = useCurrentTenantQuery();
  const { isSlackConnected } = useIsSlackConnected();
  const logoutMutation = useLogoutMutation();

  const persistedStep = currentTenantQuery.data?.onboarding?.step ?? 1;
  const currentStep = getSignupFlowStep(persistedStep);
  const hasFinishedSignupFlow =
    currentTenantQuery.data?.onboarding?.isComplete ?? false;

  if (hasFinishedSignupFlow) {
    return <Navigate to="/hm" replace />;
  }

  const logout = () => {
    logoutMutation.mutate(undefined, {
      onSuccess: () => navigate('/login', { replace: true }),
    });
  };

  const isChannelPhase =
    currentStep === signupFlowStep.slack && isSlackConnected;
  const stepKey = isChannelPhase ? 'slack-channel' : String(currentStep);

  return (
    <SignupFlowShell
      wide={isChannelPhase}
      headerRight={
        <button
          type="button"
          disabled={logoutMutation.isPending}
          onClick={logout}
          className="text-muted-foreground hover:text-foreground focus-visible:ring-ring/50 rounded-md px-2 py-1 text-[13px] font-medium transition-colors outline-none focus-visible:ring-[3px] disabled:opacity-50"
        >
          Log out
        </button>
      }
    >
      <StepTransition stepKey={stepKey}>
        <SignupFlowRouter />
      </StepTransition>
    </SignupFlowShell>
  );
};
